import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Dynamic action-item grouping state.
 *
 * Topical sub-grouping driven by gpt-5.6-luna (classify) and gpt-5.6-terra
 * (split). Two-layer model:
 *
 *   1. CLASSIFY: each action gets assigned to a coarse parent topic group
 *      (e.g. "Suspects", "Vehicles", "Scene"). Cached per action by textHash.
 *   2. SPLIT: when a parent group exceeds 5 currently-open members, the
 *      members get re-sharded into entity-anchored children. Cached per
 *      parent by member-id-set signature.
 *
 * Display is a separate concern (handled in lib/grouping/utils.ts):
 *  - Toggle off → flat (this state is invisible).
 *  - Type ≤ 8 open items → flat.
 *  - Type > 8 open items → render parent topic sub-groups; oversized parents
 *    render their entity splits as nested children.
 *
 * **Stale-response guards (codex review v2):** every classify/split request
 * generates a fresh requestId. If the response's requestId no longer matches
 * the slice's current requestId for that key, the response is dropped — the
 * action was purged or reclassified while the request was in flight.
 */

// ----- Public types ----------------------------------------------------------

export type TypeKey =
  | 'questions'
  | 'tasks'
  | 'suggestions'
  | 'deception'
  | string; // permissive — group ids embed the type key, see slugify

export interface DynamicTopicGroup {
  id: string;        // stable: `dyn:<typeKey>:<slug(label)>`
  typeKey: string;
  label: string;
}

export interface Assignment {
  parentGroupId: string;
  textHash: string;     // hash of action.details at classify time
  classifiedAt: number; // ms epoch
  /**
   * 'classifier'  — a real model classification; blocks re-classification
   *                 while the text is unchanged.
   * 'preliminary' — V2/source-metadata seed; the classifier may replace it.
   * 'fallback'    — sol r43: the backend response OMITTED or malformed this
   *                 action's row. The action is parked in the explicit
   *                 "Unclassified" group so it stays visible, but the row is
   *                 a classification FAILURE (retry machinery owns it) — it
   *                 must never block a retry the way a real result does, and
   *                 it must never be silently manufactured into 'General'.
   */
  source?: 'classifier' | 'preliminary' | 'fallback';
}

export interface DynamicSplitChild {
  label: string;
  memberIds: string[];
}

export interface DynamicSplit {
  parentId: string;
  signature: string;          // sorted memberIds joined by '|'
  children: DynamicSplitChild[];
}

export type AsyncStatus = 'idle' | 'inflight' | 'failed';

export interface ClassifyState {
  status: AsyncStatus;
  attempts: number;
  requestId?: string;
  textHash?: string; // text we asked the LLM about
  lastError?: string;
  /** Wall-clock ms of the transition INTO 'failed'. Drives the cooldown
   *  retry (2026-08-25): a terminal failure was permanent until the text
   *  changed, so a transient backend outage (the OpenAI quota breaker, now
   *  surfaced as 503 instead of a fake all-General success) left every
   *  action ungrouped forever. After DYNAMIC_GROUPING_RETRY_COOLDOWN_MS the
   *  hook treats the entry as classifiable again — one bounded retry per
   *  cooldown window, never a storm. */
  failedAt?: number;
}

export interface SplitState {
  status: AsyncStatus;
  attempts: number;
  requestId?: string;
  signature?: string;
  lastError?: string;
}

export interface DynamicGroupingState {
  groups: Record<string, DynamicTopicGroup>;
  assignments: Record<string, Assignment>;
  splits: Record<string, DynamicSplit>;
  classifyState: Record<string, ClassifyState>;
  splitState: Record<string, SplitState>;
  /**
   * Becomes true once persisted state has finished loading on a rejoin
   * (or immediately on a fresh call). The hook MUST NOT fire any LLM call
   * until this flips true — otherwise we'd duplicate-classify on refresh.
   */
  hydrated: boolean;
}

// ----- Constants -------------------------------------------------------------

/** Max retries per classify/split request before giving up. */
export const DYNAMIC_GROUPING_MAX_ATTEMPTS = 3;
/** How long a terminally-failed classification rests before one retry is
 *  allowed (see ClassifyState.failedAt). Sized well above the quota
 *  breaker's 60s cooldown so a retry lands after the backend can answer. */
export const DYNAMIC_GROUPING_RETRY_COOLDOWN_MS = 120_000;

// ----- Helpers ---------------------------------------------------------------

/** Sentinel that no real requestId will ever match. Used to invalidate
 *  in-flight requests after purge/invalidate. */
const STALE_REQUEST_SENTINEL = '__stale__';

const slugify = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'topic';

/**
 * Build a type-namespaced parent group id. Two groups with the same label in
 * different types must NOT collide — codex review caught that "Scene" under
 * Questions and "Scene" under Tasks would otherwise share the same splits
 * cache.
 */
export const buildGroupId = (typeKey: string, label: string): string =>
  `dyn:${typeKey}:${slugify(label)}`;

const initialState: DynamicGroupingState = {
  groups: {},
  assignments: {},
  splits: {},
  classifyState: {},
  splitState: {},
  hydrated: false,
};

// ----- Reducer payloads ------------------------------------------------------

interface UpsertGroupPayload {
  group: DynamicTopicGroup;
}

interface AssignActionPayload {
  actionId: string;
  parentGroupId: string;
  textHash: string;
  requestId: string; // must match current classifyState[actionId].requestId
  source?: 'classifier' | 'preliminary' | 'fallback';
}

interface MarkClassifyInflightPayload {
  actionId: string;
  requestId: string;
  textHash: string;
}

interface MarkClassifyDonePayload {
  actionId: string;
  success: boolean;
  requestId: string;
  error?: string;
}

interface MarkSplitInflightPayload {
  parentId: string;
  requestId: string;
  signature: string;
}

interface MarkSplitDonePayload {
  parentId: string;
  success: boolean;
  requestId: string;
  error?: string;
}

interface RecordSplitPayload {
  parentId: string;
  signature: string;
  children: DynamicSplitChild[];
  requestId: string; // must match current splitState[parentId].requestId
}

interface HydratePayload {
  groups: Record<string, DynamicTopicGroup>;
  assignments: Record<string, Assignment>;
  splits: Record<string, DynamicSplit>;
}

// ----- Slice -----------------------------------------------------------------

const dynamicGroupingSlice = createSlice({
  name: 'dynamicGrouping',
  initialState,
  reducers: {
    /**
     * Idempotent group upsert. Called by the hook before assigning an action
     * to a brand-new group label.
     */
    upsertGroup: (state, action: PayloadAction<UpsertGroupPayload>) => {
      const { group } = action.payload;
      state.groups[group.id] = group;
    },

    /**
     * Assign an action to a parent topic group. Stale-response guarded: if
     * the slice's current classifyState requestId does not match the payload,
     * the assignment is silently dropped (action was purged/reclassified
     * while the LLM call was in flight).
     */
    assignAction: (state, action: PayloadAction<AssignActionPayload>) => {
      const { actionId, parentGroupId, textHash, requestId, source } = action.payload;
      const inflight = state.classifyState[actionId];
      const isPreliminary = source === 'preliminary';
      const isFallback = source === 'fallback';
      if (inflight && inflight.requestId !== requestId) {
        // Preliminary seeds are local hints and may run before any classifier
        // request exists. They still must not clobber an active LLM request.
        // A FALLBACK park (sol r43) is dispatched AFTER markClassifyDone has
        // recorded the failure — the classifyState no longer carries a
        // requestId then — so it gets the same seed rule: acceptable unless a
        // newer request is actively in flight.
        const canAcceptSeed = (isPreliminary || isFallback) && inflight.status !== 'inflight';
        if (!canAcceptSeed) return; // stale — drop
      }
      if (!state.groups[parentGroupId]) {
        return; // unknown group — caller should upsertGroup first
      }
      state.assignments[actionId] = {
        parentGroupId,
        textHash,
        classifiedAt: Date.now(),
        source: isPreliminary ? 'preliminary' : isFallback ? 'fallback' : 'classifier',
      };
      if (isFallback) {
        // The park is presentation only — the failure record markClassifyDone
        // just wrote (attempts ladder, failedAt cooldown) IS the truth about
        // this action's classification and must survive the assignment.
        return;
      }
      state.classifyState[actionId] = {
        status: 'idle',
        attempts: 0,
      };
    },

    markClassifyInflight: (state, action: PayloadAction<MarkClassifyInflightPayload>) => {
      const { actionId, requestId, textHash } = action.payload;
      const prev = state.classifyState[actionId];
      // A cooldown retry restarts the attempt ladder: carrying attempts
      // already at MAX would flip straight back to 'failed' on the next
      // miss, and the SECOND outage window would burn its retry instantly.
      const baseAttempts = prev?.status === 'failed' ? 0 : prev?.attempts ?? 0;
      state.classifyState[actionId] = {
        status: 'inflight',
        attempts: baseAttempts + 1,
        requestId,
        textHash,
      };
    },

    markClassifyDone: (state, action: PayloadAction<MarkClassifyDonePayload>) => {
      const { actionId, success, requestId, error } = action.payload;
      const prev = state.classifyState[actionId];
      // Stale-response guard.
      if (prev && prev.requestId !== requestId) return;
      if (success) {
        state.classifyState[actionId] = {
          status: 'idle',
          attempts: prev?.attempts ?? 0,
        };
      } else {
        const attempts = prev?.attempts ?? 0;
        const failed = attempts >= DYNAMIC_GROUPING_MAX_ATTEMPTS;
        state.classifyState[actionId] = {
          status: failed ? 'failed' : 'idle',
          attempts,
          textHash: prev?.textHash,
          lastError: error,
          ...(failed ? { failedAt: Date.now() } : {}),
        };
      }
    },

    markSplitInflight: (state, action: PayloadAction<MarkSplitInflightPayload>) => {
      const { parentId, requestId, signature } = action.payload;
      const prev = state.splitState[parentId];
      state.splitState[parentId] = {
        status: 'inflight',
        attempts: (prev?.attempts ?? 0) + 1,
        requestId,
        signature,
      };
    },

    markSplitDone: (state, action: PayloadAction<MarkSplitDonePayload>) => {
      const { parentId, success, requestId, error } = action.payload;
      const prev = state.splitState[parentId];
      if (prev && prev.requestId !== requestId) return;
      if (success) {
        state.splitState[parentId] = {
          status: 'idle',
          attempts: prev?.attempts ?? 0,
        };
      } else {
        const attempts = prev?.attempts ?? 0;
        state.splitState[parentId] = {
          status: attempts >= DYNAMIC_GROUPING_MAX_ATTEMPTS ? 'failed' : 'idle',
          attempts,
          signature: prev?.signature,
          lastError: error,
        };
      }
    },

    /**
     * Record a successful split. Stale-response guarded against requestId,
     * AND against signature drift: if the current splitState's signature for
     * this parent has moved on since the request was issued, the result is
     * for an outdated member set and gets dropped.
     */
    recordSplit: (state, action: PayloadAction<RecordSplitPayload>) => {
      const { parentId, signature, children, requestId } = action.payload;
      const prev = state.splitState[parentId];
      if (prev && prev.requestId !== requestId) return;
      if (prev && prev.signature && prev.signature !== signature) return;
      state.splits[parentId] = { parentId, signature, children };
      state.splitState[parentId] = {
        status: 'idle',
        attempts: prev?.attempts ?? 0,
      };
    },

    /**
     * Garbage-collect assignments for actions that no longer exist anywhere.
     * Also bumps any inflight classify requestIds to a sentinel so late
     * responses can't resurrect the assignment, AND invalidates any split
     * whose signature contains a purged member id.
     */
    purgeAssignments: (state, action: PayloadAction<string[]>) => {
      const ids = action.payload;
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      for (const id of ids) {
        delete state.assignments[id];
        const prev = state.classifyState[id];
        if (prev?.status === 'inflight') {
          state.classifyState[id] = {
            ...prev,
            requestId: STALE_REQUEST_SENTINEL,
          };
        } else {
          delete state.classifyState[id];
        }
      }
      // Drop splits whose signature includes any purged member.
      for (const parentId of Object.keys(state.splits)) {
        const split = state.splits[parentId];
        if (split.children.some((c) => c.memberIds.some((m) => idSet.has(m)))) {
          delete state.splits[parentId];
          const ss = state.splitState[parentId];
          if (ss?.status === 'inflight') {
            state.splitState[parentId] = {
              ...ss,
              requestId: STALE_REQUEST_SENTINEL,
            };
          }
        }
      }
    },

    /**
     * Force-clear an assignment so the next classify pass re-runs. Bumps the
     * classifyState requestId to invalidate any in-flight response. Currently
     * unused — the textHash check in the hook handles reword detection
     * organically — but kept as an explicit invalidation hook.
     */
    invalidateAssignment: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.assignments[id];
      const prev = state.classifyState[id];
      if (prev?.status === 'inflight') {
        state.classifyState[id] = {
          ...prev,
          requestId: STALE_REQUEST_SENTINEL,
        };
      } else {
        delete state.classifyState[id];
      }
    },

    setHydrated: (state, action: PayloadAction<boolean>) => {
      state.hydrated = action.payload;
    },

    /**
     * Hydrate persisted state on rejoin. Skips classifyState/splitState —
     * those are transient and start fresh.
     */
    hydrateDynamicGrouping: (state, action: PayloadAction<HydratePayload>) => {
      state.groups = action.payload.groups || {};
      state.assignments = action.payload.assignments || {};
      state.splits = action.payload.splits || {};
      state.classifyState = {};
      state.splitState = {};
      state.hydrated = true;
    },

    resetDynamicGrouping: () => initialState,
  },
});

export const {
  upsertGroup,
  assignAction,
  markClassifyInflight,
  markClassifyDone,
  markSplitInflight,
  markSplitDone,
  recordSplit,
  purgeAssignments,
  invalidateAssignment,
  setHydrated,
  hydrateDynamicGrouping,
  resetDynamicGrouping,
} = dynamicGroupingSlice.actions;

export default dynamicGroupingSlice.reducer;
